import { Box } from "@twilio-paste/core/box";
import {
  ChatAttachment,
  ChatAttachmentDescription,
  ChatAttachmentLink,
} from "@twilio-paste/core/chat-log";
import { Spinner } from "@twilio-paste/core/spinner";
import { Stack } from "@twilio-paste/core/stack";
import { DownloadIcon } from "@twilio-paste/icons/cjs/DownloadIcon";

import { useMediaMessage } from "../../../../hooks";
import { MediaWithBlobUrl } from "../../../../hooks/useMediaMessage/definitions";
import { downloadFile } from "../../../../utils";

import { MediaMessageProps } from "../../definitions";
import styles from "./mediaMessage.module.css";

const MediaMessage = ({ message, messageDirection }: MediaMessageProps) => {
  const { files, images, isLoadingMedia } = useMediaMessage(message);
  const spinnerColor =
    messageDirection === "inbound" ? "colorText" : "colorTextInverse";

  const renderImage = (image: MediaWithBlobUrl) => {
    const isLoading = isLoadingMedia[image.sid];

    return (
      <Box key={image.sid} className={styles.imageContainer}>
        {isLoading ? (
          <Spinner
            decorative={false}
            color={spinnerColor}
            size="sizeIcon60"
            title="Loading"
          />
        ) : (
          <Box
            as="img"
            src={image.blobUrl || ""}
            className={styles.image}
            onClick={() =>
              downloadFile(image.blobUrl || "", image.filename || "")
            }
          />
        )}
      </Box>
    );
  };

  const renderFile = (file: MediaWithBlobUrl) => {
    const isLoading = isLoadingMedia[file.sid];
    const fileSizeMb = Math.round((file.size / 1024 ** 2) * 100) / 100;

    const attachmentIcon = isLoading ? (
      <Spinner decorative={false} title="Loading" />
    ) : (
      <DownloadIcon decorative />
    );

    if (file.blobUrl && file.filename) {
      return (
        <ChatAttachment key={file.filename} attachmentIcon={attachmentIcon}>
          <ChatAttachmentLink href={file.blobUrl} download={file.filename}>
            {file.filename}
          </ChatAttachmentLink>
          <ChatAttachmentDescription>{`${fileSizeMb} MB`}</ChatAttachmentDescription>
        </ChatAttachment>
      );
    }
  };

  const renderImages = () => (
    <Stack orientation="vertical" spacing="space30">
      {images.map(renderImage)}
    </Stack>
  );

  const renderFiles = () => (
    <Stack orientation="vertical" spacing="space30">
      {files.map(renderFile)}
    </Stack>
  );

  return (
    <Box marginY="space30">
      {renderImages()}
      {renderFiles()}
    </Box>
  );
};

export default MediaMessage;
